import React, { useEffect, useState } from 'react';

/**
 * Product photo that looks the same size in every card.
 *
 * The catalogue's photos are cropped inconsistently: some packshots fill the
 * frame edge to edge, others sit small in a lot of white. In identical boxes
 * that reads as "some products are huge, some are tiny". This component
 * measures where the product actually is in the photo (a 64px sample, once per
 * URL, cached) and zooms / re-centres loosely cropped photos so every product
 * fills roughly the same share of its box. Tight photos are left alone; nothing
 * is ever shrunk.
 *
 * Measuring needs to read pixels, which the browser only allows for
 * same-origin (or CORS-enabled) images. When it isn't allowed the photo simply
 * renders as before — this is progressive enhancement, never a failure.
 *
 * Props:
 *   pad        inner padding as a fraction of the box (0.13 = 13%)
 *   className  extra classes for the <img> (hover effects etc.)
 */

const cache = new Map(); // src -> { k, tx, ty } | null
const pending = new Map(); // src -> Promise

const SAMPLE = 64;          // analysis resolution
const TARGET = 0.94;        // share of the padded box the product should fill
const MAX_ZOOM = 1.65;      // never blow a photo up past this

function analyse(src) {
  if (cache.has(src)) return Promise.resolve(cache.get(src));
  if (pending.has(src)) return pending.get(src);

  const p = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      try {
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        if (!W || !H) return resolve(null);
        const r = SAMPLE / Math.max(W, H);
        const w = Math.max(1, Math.round(W * r));
        const h = Math.max(1, Math.round(H * r));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h); // throws if tainted

        // Bounding box of "product" pixels: not transparent, not near-white.
        let x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            const a = data[i + 3];
            const isBg = a < 20 || (data[i] > 238 && data[i + 1] > 238 && data[i + 2] > 238);
            if (!isBg) {
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
        }
        if (x1 < 0) return resolve(null);

        // Where the product sits inside a square box the photo is
        // `object-contain`ed into (box side = 1).
        const fit = 1 / Math.max(w, h);
        const dw = w * fit;
        const dh = h * fit;
        const ox = (1 - dw) / 2;
        const oy = (1 - dh) / 2;
        const cw = (x1 - x0 + 1) * fit;
        const ch = (y1 - y0 + 1) * fit;
        const cx = ox + (x0 * fit) + cw / 2;
        const cy = oy + (y0 * fit) + ch / 2;

        const k = Math.min(MAX_ZOOM, Math.max(1, TARGET / Math.max(cw, ch)));
        // Translate so the product's centre lands on the box centre after the
        // zoom (translate runs after scale in `translate() scale()`).
        const tx = -k * (cx - 0.5);
        const ty = -k * (cy - 0.5);
        const worthIt = k > 1.03 || Math.abs(tx) > 0.02 || Math.abs(ty) > 0.02;
        resolve(worthIt ? { k, tx, ty } : null);
      } catch {
        resolve(null); // cross-origin (tainted canvas) or anything else
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  }).then((result) => {
    cache.set(src, result);
    pending.delete(src);
    return result;
  });

  pending.set(src, p);
  return p;
}

export default function ProductImage({ src, alt = '', pad = 0.13, className = '', loading = 'lazy' }) {
  const [fitInfo, setFitInfo] = useState(() => (cache.has(src) ? cache.get(src) : null));

  useEffect(() => {
    let alive = true;
    if (!src) return undefined;
    if (cache.has(src)) {
      setFitInfo(cache.get(src));
      return undefined;
    }
    analyse(src).then((res) => { if (alive) setFitInfo(res); });
    return () => { alive = false; };
  }, [src]);

  // Translation is a share of the padded area; the element's % is of its full
  // box (padding included), hence the (1 - 2 * pad) factor.
  const inner = 1 - 2 * pad;
  const style = { padding: `${pad * 100}%` };
  if (fitInfo) {
    style.transform = `translate(${fitInfo.tx * inner * 100}%, ${fitInfo.ty * inner * 100}%) scale(calc(${fitInfo.k} * var(--img-hover, 1)))`;
  }

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      draggable={false}
      style={style}
      className={`h-full w-full object-contain mix-blend-multiply transition-transform duration-500 ease-out [transform:scale(var(--img-hover,1))] ${className}`}
    />
  );
}
